// sounds.js
// Small Web Audio API utility for Aria chat sound effects.
// Pure synthesis — no audio files to ship.
//
// Exports:
//   playSend()        → short rising chirp when the user sends a message
//   startThinking()   → looping subtle "..." ticks while Aria is replying
//   stopThinking()    → stops the loop
//   playReceive()     → soft two-tone chime when Aria's reply arrives
//   setSoundsEnabled(boolean) → master mute toggle (persisted in localStorage)
//   isSoundsEnabled() → read current setting

const ENABLED_KEY = 'aria_sounds_enabled';

let _ctx = null;
function ctx() {
  if (_ctx) return _ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
  } catch (_) {
    _ctx = null;
  }
  return _ctx;
}

/** Browsers suspend AudioContext until a user gesture. Call this anywhere
 *  inside a click/keydown handler to wake it up. Safe to call repeatedly. */
function ensureAwake() {
  const c = ctx();
  if (!c) return;
  if (c.state === 'suspended') {
    try { c.resume(); } catch (_) {}
  }
}

export function isSoundsEnabled() {
  try {
    const v = localStorage.getItem(ENABLED_KEY);
    return v === null ? true : v === '1';
  } catch (_) { return true; }
}

export function setSoundsEnabled(on) {
  try { localStorage.setItem(ENABLED_KEY, on ? '1' : '0'); } catch (_) {}
}

/** Tiny helper: play a single tone with an envelope. */
function tone({ freq = 440, dur = 0.12, type = 'sine', vol = 0.12, attack = 0.005, decay = 0.06, glideTo = null, startOffset = 0 }) {
  const c = ctx();
  if (!c) return;
  ensureAwake();
  const t0 = c.currentTime + startOffset;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) {
    osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  }
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** SEND — short rising chirp, like a "whoosh" / "out" cue. */
export function playSend() {
  if (!isSoundsEnabled()) return;
  // Slight body via two layered sine glides — feels softer than a single tone.
  tone({ freq: 520, glideTo: 880, dur: 0.16, type: 'sine',     vol: 0.10 });
  tone({ freq: 260, glideTo: 440, dur: 0.16, type: 'triangle', vol: 0.05 });
}

/** RECEIVE — gentle two-note bell-like chime. Aria has spoken back. */
export function playReceive() {
  if (!isSoundsEnabled()) return;
  // Major 6th interval (G5 → E6) — feels warm + open, like iMessage receive.
  tone({ freq: 784, dur: 0.22, type: 'sine',     vol: 0.10,  attack: 0.004 });
  tone({ freq: 1318, dur: 0.30, type: 'sine',    vol: 0.07,  attack: 0.006, startOffset: 0.06 });
  // A little sub fundamental for warmth
  tone({ freq: 392, dur: 0.28, type: 'triangle', vol: 0.04,  attack: 0.005 });
}

// ── Thinking loop ───────────────────────────────────────────────────────
// Plays three quick soft ticks (.  .  .) every ~1.4 seconds. Subtle —
// you should feel it more than hear it.
let thinkingTimer = null;

function thinkingTick() {
  // Three quick ticks in a row — like the "..." in chat bubbles.
  tone({ freq: 600, dur: 0.06, type: 'sine', vol: 0.045, attack: 0.002, startOffset: 0.00 });
  tone({ freq: 600, dur: 0.06, type: 'sine', vol: 0.045, attack: 0.002, startOffset: 0.18 });
  tone({ freq: 600, dur: 0.06, type: 'sine', vol: 0.045, attack: 0.002, startOffset: 0.36 });
}

export function startThinking() {
  if (!isSoundsEnabled()) return;
  if (thinkingTimer) return; // already running
  thinkingTick();
  thinkingTimer = setInterval(() => {
    if (!isSoundsEnabled()) { stopThinking(); return; }
    thinkingTick();
  }, 1400);
}

export function stopThinking() {
  if (thinkingTimer) {
    clearInterval(thinkingTimer);
    thinkingTimer = null;
  }
}
